import { api } from './api';
import type {
  SmsTemplate, SmsTemplateCreateRequest, SmsTemplateUpdateRequest, SmsTemplateSyncResult,
  SmsTemplateLookup,
} from '../types/sms-template.types';

/**
 * DLT template management.
 *
 * The list is served from our own mirror rather than the provider, so the screen
 * renders without a third-party round trip and still works while MSG91 is slow or
 * unreachable. `sync` is the explicit reconciliation.
 */
export const smsTemplateService = {
  /**
   * The templates registered for one project.
   *
   * `appId` is for the pickers: a notification master is authored FOR a project,
   * which need not be the project the author is signed in to, and the templates
   * it may send with are that project's — they resolve against that project's
   * gateway account at send time. Omitted, the caller's own project is used.
   */
  list: async (includeInactive = false, appId?: string | null): Promise<SmsTemplate[]> => {
    const res = await api.get<{ data: SmsTemplate[] }>('/api/v1/sms/templates', {
      params: { includeInactive, ...(appId ? { appId } : {}) },
    });
    return res.data?.data ?? [];
  },

  create: async (data: SmsTemplateCreateRequest): Promise<SmsTemplate> => {
    const res = await api.post<{ data: SmsTemplate }>('/api/v1/sms/templates', data);
    return res.data?.data;
  },

  /**
   * Correct a registered template's ids.
   *
   * The operator's DLT id once it is issued, and the gateway's own id when
   * registration failed to capture it. Not the wording: DLT requires a fresh
   * registration for any change to the text.
   */
  update: async (id: string, data: SmsTemplateUpdateRequest): Promise<SmsTemplate> => {
    const res = await api.patch<{ data: SmsTemplate }>(`/api/v1/sms/templates/${id}`, data);
    return res.data?.data;
  },

  /**
   * Make a template usable, whichever side it already exists on.
   *
   * With a `providerTemplateId` nothing is submitted — the row is linked to the
   * template the gateway already holds and its wording is read from there. Without
   * one, the wording is new and gets registered. The branch matters: on a DLT
   * account, re-registering text that already exists is days of operator review for
   * a duplicate nobody wanted.
   */
  ensure: async (data: {
    name: string;
    providerTemplateId?: string;
    body?: string;
    senderId?: string;
    dltTemplateId?: string;
    smsType?: string;
  }): Promise<SmsTemplate> => {
    const res = await api.post<{ data: SmsTemplate }>('/api/v1/sms/templates/ensure', data);
    return res.data.data;
  },

  /**
   * What the gateway holds under one template id.
   *
   * Rejects (404) when the account does not know the id, which is the answer to
   * "is this the right id" — asked before the id is recorded rather than after a
   * send addressed to nothing is accepted and dropped.
   */
  lookup: async (providerTemplateId: string): Promise<SmsTemplateLookup> => {
    const res = await api.get<{ data: SmsTemplateLookup }>(
      '/api/v1/sms/templates/lookup', { params: { providerTemplateId } });
    return res.data?.data;
  },

  /** Ask the provider what became of the templates still awaiting an operator
   *  id, and settle each from the answer. */
  checkApprovals: async (): Promise<SmsTemplateSyncResult> => {
    const res = await api.post<{ data: SmsTemplateSyncResult }>('/api/v1/sms/templates/sync');
    return res.data?.data;
  },
};
